import type { Messages } from '../catalogue.en'

/**
 * The Control Center's external-service sections: Telegram, Google Document
 * AI, Google sign-in, AI configuration and stored secrets. Mirrors
 * `en/controlCenterIntegrations.ts`.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing
 * imports this module directly: consumers ask the catalogue, so how the
 * messages are stored stays this layer's business.
 */
export const controlCenterIntegrations: Partial<Messages> = {
  'ai.functions.function': "פונקציה",
  'ai.functions.pipeline': "תהליך עיבוד",
  'ai.functions.pipelineFor': "תהליך עיבוד עבור {name}",
  'ai.functions.prompts': "הנחיות",
  'ai.functions.parametersFor': "פרמטרים: {name}",
  'ai.functions.enabled': "מופעלת",
  'ai.functions.enabledFor': "הפעלת {name}",
  'ai.functions.unsaved': "לא נשמר",
  'ai.functions.pendingFor': "בחירה שלא נשמרה: {name}.",
  'ai.functions.apply': "החלת בחירת תהליך העיבוד",
  'ai.functions.cancel': "ביטול שינויי הבחירה",
  'ai.functions.applied': "נשמר. בקשות חדשות ישתמשו בבחירה זו.",
  'ai.functions.noPrompts': "ללא הנחיות מהמאגר",
  'ai.functions.promptUnavailable': "הפרטים אינם זמינים",
  'ai.functions.fixedSettings': "פרמטרי הביצוע האלה זמינים לקריאה בלבד.",
  'ai.functions.empty': "אין פונקציות בינה מלאכותית זמינות",
  'ai.functions.governmentId': "חילוץ פרטי תעודת זהות",
  'ai.functions.insurance': "חילוץ פרטי פוליסת ביטוח",
  'ai.functions.transcription': "תמלול דיבור לטקסט",
  'ai.functions.transcriptionStage': "תמלול דיבור",
  'ai.functions.dataOCR': "Google מקבלת תמונות של עמודי המסמך. OpenAI מקבלת את הטקסט שזוהה ותמונות עמודים מנורמלות לאימות.",
  'ai.functions.dataDictation': "OpenAI מקבלת את ההקלטה, ובהמשך את התמלול ואת סכמת הטופס הקליני לחילוץ שדות.",
  'ai.functions.dataAudio': "OpenAI מקבלת את הקלטת השמע.",
  'ai.functions.dataText': "OpenAI מקבלת את הטקסט שנבחר ואת קובצי התמיכה הלשוניים המתאימים.",
  'ai.functions.dataDocument': "OpenAI מקבלת תוכן מסמך, כולל תמונות עמודים הנדרשות לפונקציה זו.",
  'ai.functions.dataLabGooglePages': "Google Document AI מקבלת כל עמוד בנפרד. OpenAI מקבלת את הטקסט המזוהה של כל עמוד, ואחר כך את התמונות והטקסט של העמודים שאישרתם.",
  'ai.functions.dataQuestion': "OpenAI מקבלת את המסמך הקליני, את השאלה ואת השאלות הקודמות של הרופא באירוע.",
  'ai.functions.dataClinical': "OpenAI מקבלת מידע קליני מהאירוע לצורך הצעת המלצות.",
  'ai.functions.dataFilters': "OpenAI מקבלת כותרות עמודות ותיאור חיפוש; שורות הטבלה אינן נשלחות.",
  'ai.functions.dataNames': "OpenAI מקבלת את טקסט המקור ואת השפות המבוקשות.",
  'ai.functions.dataIssue': "OpenAI מקבלת את תיאור הפנייה; קבצים מצורפים אינם נשלחים.",
  'ai.functions.dataExternalEntries': "OpenAI מקבלת את הטקסט שזוהה בעמודי המסמכים שנבחרו כדי לסמן אותם, לקבץ אותם לאירועי טיפול ולנסח רשומה אחת לכל אירוע.",
  'ai.functions.dataSummary': "OpenAI מקבלת את השדות המלאים של רשומות התיק ואת הטקסט שזוהה במסמכי המטופל.",
  'ai.functions.dataUnknown': "הנתונים הנדרשים לפונקציה נשלחים לספקים הרשומים.",
  'ai.functions.title': "פעולות בינה מלאכותית",
  'ai.functions.description': 'השבתה חוסמת בקשות חדשות; עיבוד שכבר התקבל עשוי להסתיים.',
  'ai.functions.loading': "טוען הגדרות פעולות…",
  'ai.functions.unavailable': "הגדרות הפעולות אינן זמינות. נסו שוב לאחר טעינתן.",
  'ai.functions.disabled': "מושבת",
  'ai.functions.disabledFor': "השבתה: {name}",
  'ai.functions.disabledHint': "מושבת בהגדרות הבינה המלאכותית במרכז הבקרה.",
  'ai.functions.external': "הנתונים נשלחים לספקים חיצוניים:",
  'ai.functions.local': "העיבוד נשאר בסביבה המקומית.",
  'ai.functions.googleOpenai': "זיהוי Google ואימות OpenAI",
  'ai.functions.googleIDOpenai': "זיהוי Google וחילוץ OpenAI",
  'ai.functions.googlePagesOpenai': "Google עמוד אחר עמוד וחילוץ OpenAI",
  'ai.functions.dataIDGoogleLocal': "Google מקבל את עמודי המסמך לזיהוי טקסט. המודל המקומי מקבל את טקסט הזיהוי ואת התמונות המיושרות; הם אינם נשלחים ל־OpenAI.",
  'ai.functions.dataIDGoogleOpenai': "Google מקבל את עמודי המסמך לזיהוי טקסט. OpenAI מקבל את טקסט הזיהוי ואת התמונות המיושרות לחילוץ פרטי הזיהוי.",
  'ai.functions.dataInsuranceGoogleOpenai': "Google מקבל את עמודי המסמך לזיהוי טקסט. OpenAI מקבל את טקסט הזיהוי ואת התמונות המיושרות לחילוץ פרטי פוליסת הביטוח.",
  'ai.functions.googleLocal': "זיהוי Google ומודל מקומי",
  'ai.functions.localOpenai': "יישור מקומי וזיהוי OpenAI",
  'ai.functions.ocr': "זיהוי טקסט במסמך (OCR)",
  'ai.functions.dictation': "מילוי רשומה קלינית מהכתבה",
  'ai.functions.translateDocument': "תרגום מסמכים",
  'ai.functions.translateRecord': "תרגום רשומה רפואית",
  'ai.functions.summary': "סיכום מסמכים",
  'ai.functions.questions': "שאלות על מסמכים רפואיים",
  'ai.functions.review': "הצעת המלצות קליניות",
  'ai.functions.filters': "הצעת מסננים לטבלה",
  'ai.functions.localizedText': "הצעות לתרגום שמות",
  'ai.functions.issueTitle': "הצעות לכותרות משימות",
  'ai.functions.unknown': "פעולה או מסלול שאינם נתמכים — עדכנו את היישום",
  'ai.functions.unsavedIntent': "הגדרות פעולות בינה מלאכותית",
  'ai.functions.reload': "טעינת ההגדרות השמורות במקום הטיוטה",
  'ai.functions.reloaded': "ההגדרות השמורות נטענו.",
  'ai.functions.reloadFailed': "לא ניתן לטעון את ההגדרות. הטיוטה נשמרה.",
  'telegramLink.title': 'Telegram',
  'telegramLink.loading': 'טוען את חיבור Telegram',
  'telegramLink.loadFailed': 'לא ניתן לטעון את חיבור Telegram.',
  'telegramLink.connected': 'מחובר',
  'telegramLink.notConnected': 'לא מחובר',
  'telegramLink.createInvite': 'יצירת קישור הזמנה',
  'telegramLink.creating': 'יוצר קישור',
  'telegramLink.inviteLink': 'קישור הזמנה חד־פעמי ל-Telegram',
  'telegramLink.copyInvite': 'העתקת קישור ההזמנה',
  'telegramLink.openTelegram': 'פתיחה ב-Telegram',
  'telegramLink.expiresInDay': 'תוקף הקישור החד־פעמי יפוג בעוד 24 שעות.',
  'telegramLink.copied': 'קישור ההזמנה הועתק',
  'telegramLink.copyFailed': 'לא ניתן להעתיק את קישור ההזמנה.',
  'telegramLink.botUnavailable': 'בוט ה-Telegram של המרפאה עדיין לא הוגדר.',
  'telegramLink.inviteFailed': 'לא ניתן ליצור את קישור ההזמנה.',
  'telegramLink.disconnect': 'ניתוק',
  'telegramLink.disconnecting': 'מנתק',
  'telegramLink.disconnectFailed': 'לא ניתן לנתק את Telegram.',
  'whatsappLink.title': 'WhatsApp',
  'whatsappLink.loading': 'טוען את חיבור WhatsApp',
  'whatsappLink.loadFailed': 'לא ניתן לטעון את חיבור WhatsApp.',
  'whatsappLink.connected': 'מחובר',
  'whatsappLink.notConnected': 'לא מחובר',
  'whatsappLink.createInvite': 'יצירת קישור הזמנה',
  'whatsappLink.creating': 'יוצר קישור',
  'whatsappLink.inviteLink': 'קישור הזמנה חד־פעמי ל-WhatsApp',
  'whatsappLink.copyInvite': 'העתקת קישור ההזמנה',
  'whatsappLink.openWhatsApp': 'פתיחה ב-WhatsApp',
  'whatsappLink.sendToLink':
    'הקישור פותח שיחת WhatsApp עם מספר המרפאה. שליחת ההודעה המוכנה מקשרת את הטלפון שממנו היא נשלחה.',
  'whatsappLink.expiresInDay': 'תוקף הקישור החד־פעמי יפוג בעוד 24 שעות.',
  'whatsappLink.copied': 'קישור ההזמנה הועתק',
  'whatsappLink.copyFailed': 'לא ניתן להעתיק את קישור ההזמנה.',
  'whatsappLink.senderUnavailable': 'מספר השולח של WhatsApp במרפאה עדיין לא הוגדר.',
  'whatsappLink.inviteFailed': 'לא ניתן ליצור את קישור ההזמנה.',
  'whatsappLink.disconnect': 'ניתוק',
  'whatsappLink.disconnecting': 'מנתק',
  'whatsappLink.disconnectFailed': 'לא ניתן לנתק את WhatsApp.',

  'storedSecret.updated': 'עודכן',
  'storedSecret.length': '{length} תווים',


  'googleDocAI.title': 'Google Document AI',
  'googleDocAI.loading': 'טוען את מצב Google Document AI',
  'googleDocAI.unavailable': 'אחסון הסודות המוצפן של המרפאה אינו מוגדר.',
  'googleDocAI.loadFailed': 'לא ניתן לטעון את מצב Google Document AI.',
  'googleDocAI.serviceAccount': 'חשבון שירות',
  'googleDocAI.project': 'פרויקט',
  'googleDocAI.key': 'תוכן קובץ מפתח חשבון השירות',
  'googleDocAI.storedKey': 'מפתח פרטי',
  'googleDocAI.set': 'הגדרת מפתח חשבון השירות',
  'googleDocAI.change': 'החלפת מפתח חשבון השירות',
  'googleDocAI.openFile': 'פתיחת קובץ מפתח',
  'googleDocAI.keyFilePicker': 'בחירת קובץ מפתח חשבון השירות',
  'googleDocAI.fromFile': 'נקרא מ-{name}',
  'googleDocAI.fileReadFailed': 'לא ניתן לקרוא את הקובץ שנבחר.',
  'googleDocAI.location': 'מיקום המעבד',
  'googleDocAI.processorId': 'מזהה המעבד',
  'googleDocAI.unsavedIntent': 'מפתח Google Document AI',
  'googleDocAI.save': 'שמירת המפתח',
  'googleDocAI.saving': 'בודק ושומר',
  'googleDocAI.clear': 'הסרת המפתח',
  'googleDocAI.clearing': 'מסיר',
  'googleDocAI.saved': 'מפתח Google Document AI נשמר',
  'googleDocAI.cleared': 'מפתח Google Document AI הוסר',
  'googleDocAI.rejected': 'זה אינו תוכן של קובץ מפתח חשבון שירות של Google.',
  'googleDocAI.settingsRejected': 'מיקום המעבד או המזהה אינם תקינים.',
  'googleDocAI.saveFailed': 'לא ניתן לשמור את מפתח Google Document AI.',
  'googleDocAI.clearFailed': 'לא ניתן להסיר את מפתח Google Document AI.',

  "googleSignIn.title": "כניסת מטופלים באמצעות Google",
  'googleSignIn.loading': 'טוען את הגדרת הכניסה עם Google',
  'googleSignIn.loadFailed': 'לא ניתן לטעון את הגדרת הכניסה עם Google.',
  'googleSignIn.clientId': 'מזהה לקוח OAuth לאתר',
  'googleSignIn.hint':
    'מזהה הלקוח הציבורי מ-Google Cloud Console, המסתיים ב-‎.apps.googleusercontent.com. כפתור Google מוצג בדף הקליטה הציבורי רק כאשר המזהה מוגדר.',
  'googleSignIn.set': 'הגדרת מזהה לקוח',
  'googleSignIn.change': 'החלפת מזהה הלקוח',
  'googleSignIn.unsavedIntent': 'מזהה לקוח לכניסה עם Google',
  'googleSignIn.save': 'שמירת המזהה',
  'googleSignIn.saving': 'בודק ושומר',
  'googleSignIn.clear': 'הסרת המזהה',
  'googleSignIn.clearing': 'מסיר',
  'googleSignIn.saved': 'מזהה הלקוח לכניסה עם Google נשמר',
  'googleSignIn.cleared': 'מזהה הלקוח לכניסה עם Google הוסר',
  'googleSignIn.rejected': 'זה אינו מזהה לקוח אינטרנט של Google המסתיים ב-‎.apps.googleusercontent.com.',
  'googleSignIn.saveFailed': 'לא ניתן לשמור את מזהה הלקוח לכניסה עם Google.',
  'googleSignIn.clearFailed': 'לא ניתן להסיר את מזהה הלקוח לכניסה עם Google.',

  'ai.title': 'בינה מלאכותית',
  'ai.views.label': 'תת-סעיפים של בינה מלאכותית',
  'ai.views.settings': "פונקציות",
  'ai.views.settingsDescription': "תהליכי עיבוד, הנחיות ופרמטרי ביצוע",
  'ai.views.prompts': 'פרומפטים',
  'ai.views.promptsDescription': 'המרשם והפרמטרים שלו למרפאה הזו',
  'ai.views.files': 'קבצים',
  'ai.views.filesDescription': 'מודולים ומילוני מונחים שכל הרצה מצרפת',
  'ai.views.credentials': 'פרטי גישה',
  'ai.views.credentialsDescription': 'מפתחות ופרטי גישה לשירותים',
  'ai.provider.title': 'מנועים לעיבוד מסמכים',
  'ai.provider.description':
    'כל פעולה רצה במקום שהמרפאה שולחת אליו. שלוש הבחירות נשמרות יחד.',
  'ai.provider.openai': 'OpenAI',
  'ai.provider.local': 'מקומי',
  'ai.provider.loading': 'טוען מנועים לעיבוד מסמכים',
  'ai.provider.loadFailed': 'לא ניתן לטעון את הגדרות פונקציות AI',
  'ai.provider.save': 'שמירת הגדרות AI',
  'ai.provider.saving': 'שומר הגדרות AI',
  'ai.provider.saved': 'הגדרות פונקציות AI נשמרו',
  'ai.provider.saveFailed': 'לא ניתן לשמור את הגדרות פונקציות AI',
  'ai.provider.saveForbidden': 'אין לך הרשאה לשנות את הגדרות פונקציות AI במרפאה הזו',
  'ai.provider.saveConflict': 'מישהו אחר שינה את הגדרות AI האלה. רעננו את הדף ובחרו שוב.',
  'ai.provider.unsavedIntent': 'בחירת מנועי AI \u2014 טרם נשמרה',
  'ai.backend.deidentification': 'אנונימיזציה',
  'ai.backend.deidentificationNote': 'מסירה את זהות המטופל מהמסמך לפני שהוא יוצא מהמרפאה.',
  'ai.backend.classification': 'סיווג',
  'ai.backend.classificationNote': 'קובע מהו המסמך שהועלה, כדי שיתויק במקום הנכון.',
  'ai.backend.laboratory': 'חילוץ תוצאות מעבדה',
  'ai.backend.laboratoryNote': 'קורא ערכי בדיקות מדוח מעבדה אל תיק המטופל.',
  'ai.tester.title': 'בודק מסמכים',
  'ai.tester.open': 'פתיחת בודק המסמכים',
  'ai.tester.hint': 'סביבת Gradio למפתחים בלבד לסיווג, חילוץ תוצאות מעבדה ואנונימיזציה.',
  'ai.prompts.title': 'מרשם הפרומפטים',
  'ai.prompts.note':
    'הפרומפטים נרשמים על ידי שירות ה-AI. הפרמטרים הרשומים כאן חלים על המרפאה הזו בלבד.',
  'ai.loading': 'טוען פרומפטים של AI',
  'ai.loadFailed': 'לא ניתן לטעון פרומפטים של AI',
  'ai.forbidden': 'אין לך הרשאה לקרוא פרומפטים של AI במרפאה הזו',
  'ai.empty': 'לא רשומים פרומפטים של AI',
  'ai.prompt': 'פרומפט',
  'ai.mode': 'מצב',
  'ai.modeResponses': 'Responses API',
  'ai.modeLocalVlm': "מודל ראייה מקומי",
  'ai.modeAgent': 'סוכן',
  'ai.model': 'מודל',
  'ai.turns': 'סבבים',
  'ai.output': 'אסימוני פלט',
  'ai.tools': 'כלים',
  'ai.providerState': 'מצב ספק',
  'ai.providerStateOn': 'פעיל',
  'ai.providerStateOff': 'כבוי',
  'ai.langfuse': 'Langfuse',
  'ai.reasoning': 'חשיבה',
  'ai.reasoningUnset': 'לא הוגדר',
  'ai.verbosity': 'רמת פירוט',
  'ai.verbosityUnset': 'לא הוגדר',
  'ai.serviceTier': 'רמת שירות',
  'ai.timeout': 'פסק זמן, שניות',
  'ai.webSearch': 'חיפוש באינטרנט',
  'ai.webSearchContext': 'הקשר חיפוש באינטרנט',
  'ai.source': 'פרמטרים',
  'ai.sourceCustom': 'מותאם',
  'ai.sourceDefault': 'ברירת מחדל',
  'ai.tableLabel': 'פרומפטים של AI',
  'ai.actions': 'פעולות',
  'ai.editNamed': 'עריכת {name}',
  'ai.emptyDetail': 'הפרומפטים נרשמים בשירות ה-AI ולא מהמסך הזה.',
  'ai.noMatches': 'אין פרומפטים של AI התואמים למסננים',
  'ai.noMatchesDetail': 'נקה את המסננים כדי לראות את כל הפרומפטים הרשומים.',
  'ai.editorTitle': 'פרמטרים של הרצה',
  'ai.editorNote': 'טקסט הפרומפט וסכמת התוצאה קבועים בשירות ה-AI. הפרמטרים האלה חלים על המרפאה הזו בלבד.',
  'ai.range': 'מותר:',
  'ai.outputContract': 'סכמת התוצאה',
  'ai.outputContractFixed': 'קבועה בפרומפט',
  'ai.save': 'שמירה',
  'ai.saving': 'שומר',
  'ai.saved': 'נשמר',
  'ai.saveFailed': 'לא ניתן לשמור את פרמטרי ההרצה',
  'ai.saveForbidden': 'אין לך הרשאה לשנות פרמטרים של פרומפטים במרפאה הזו',
  'ai.saveConflict': 'מישהו אחר שינה את הפרמטרים האלה. סגור את הפאנל ופתח אותו מחדש.',
  'ai.invalidDraft': 'ערך נמצא מחוץ לטווח המותר',
  'ai.unsavedIntent': 'הגדרות פרומפט AI \u2014 שינויים שלא נשמרו',
  'ai.reset': 'שחזור ברירת מחדל',
  'ai.resetting': 'משחזר',
  'ai.files.title': 'קובצי תמיכה לפרומפטים',
  'ai.files.note':
    'הרצות תרגום והכתבה מצרפות את הקבצים האלה לפרומפט: מודולים של שפת מקור, מודולים של סגנון יעד ומילוני מונחים. כל קובץ מועלה ל-OpenAI פעם אחת לכל חשבון, ולאחר מכן מפנים אליו לפי מזהה.',
  'ai.files.loading': 'טוען קובצי פרומפט של AI',
  'ai.files.loadFailed': 'לא ניתן לטעון את קובצי הפרומפט של AI',
  'ai.files.tableLabel': 'קובצי פרומפט של AI',
  'ai.files.empty': 'לא רשומים קובצי תמיכה',
  'ai.files.emptyDetail': 'קובצי התמיכה מגיעים עם מערך הנתונים המסופק מראש.',
  'ai.files.file': 'קובץ',
  'ai.files.slot': 'תפקיד',
  'ai.files.slotSource': 'שפת מקור',
  'ai.files.slotTarget': 'סגנון יעד',
  'ai.files.slotGlossary': 'מילון מונחים',
  'ai.files.language': 'שפה',
  'ai.files.size': 'גודל',
  'ai.files.source': 'תוכן',
  'ai.files.sourceSeed': 'מסופק מראש',
  'ai.files.sourceCustom': 'מותאם',
  'ai.files.openai': 'OpenAI',
  'ai.files.openaiCurrent': 'הועלה',
  'ai.files.openaiStale': 'ממתין להעלאה מחדש',
  'ai.files.downloadNamed': 'הורדת {name}',
  'ai.files.replaceNamed': 'החלפת {name}',
  'ai.files.replacePicker': 'בחירת קובץ חלופי',
  'ai.files.replaced': 'הקובץ {file} נשמר. ההרצה הבאה של הפרומפט תעלה אותו ל-OpenAI.',
  'ai.files.replaceInvalid': 'הקובץ החלופי חייב להיות קובץ טקסט UTF-8 עד 1 MiB.',
  'ai.files.replaceConflict': 'מישהו אחר שינה את הקובץ הזה. הרשימה מציגה את התוכן הנוכחי; בחר את הקובץ שוב.',
  'ai.files.replaceFailed': 'לא ניתן להחליף את {file}',
}
